import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McTopBar } from "@maersk-global/mds-react-wrapper/components-core/mc-top-bar";
import { McMenu } from "@maersk-global/mds-react-wrapper/components-core/mc-menu";
import "./Charges.css";
import ActionModal from "../Model/Model";
import ApproveModal from "../Model/ApproveModel";
import { useMsal, useAccount } from '@azure/msal-react';
import CommentsHistoryModal from "../Model/Comments";
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core-tag';
import PoNumberModal from "../Model/PoModel";
import InvoiceModal from "../Model/InvoiceModel";
import { McToast } from "@maersk-global/mds-react-wrapper/components-core/mc-toast";
import { McNotification } from "@maersk-global/mds-react-wrapper/components-core/mc-notification";
import { AL } from "../../al";
import { useUser } from "../../contexts/UserContext";

// Component for text with tooltip
const TextWithTooltip = ({ text, maxLength = 25 }) => {
  if (!text || text === "NA") return text;

  const needsTruncation = text.length > maxLength;
  const displayText = needsTruncation ? text.substring(0, maxLength) + "..." : text;

  return (
    <span title={text} style={{ cursor: needsTruncation ? 'help' : 'default' }}>
      {displayText}
    </span>
  );
};

const ChargesPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const { userInfo, roles, loading: userLoading } = useUser();

  const [poModalOpen, setPoModalOpen] = useState(false);
  const [commentsChargeId, setCommentsChargeId] = useState(null);
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [selectedPONumber, setSelectedPONumber] = useState(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState(null);

  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [downloadAttempted, setDownloadAttempted] = useState(false);
  const [hasFiles, setHasFiles] = useState(false);

  const handleViewComments = (chargeId) => {
    setCommentsChargeId(chargeId);
    setCommentsModalOpen(true);
  };

  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [parentNotice, setParentNotice] = useState(location.state || null); // Parent notice details

  useEffect(() => {
    const parentNameForTitle =
      parentNotice?.Parent_Name ||
      parentNotice?.Parent_Name__c ||
      parentNotice?.Name ||
      id;
    if (!parentNameForTitle) return;

    const previousTitle = document.title;
    document.title = parentNameForTitle;

    return () => {
      document.title = previousTitle;
    };
  }, [id, parentNotice?.Parent_Name, parentNotice?.Parent_Name__c, parentNotice?.Name]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalActionType, setModalActionType] = useState(null);
  const [selectedChargeId, setSelectedChargeId] = useState(null);
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [isApprovalTypeBot, setIsApprovalTypeBot] = useState(false); // Track if it's cost approval (isBot=true) or event approval (isBot=false)
  const [isActionTypeBot, setIsActionTypeBot] = useState(false); // Track if it's cost action (isBot=true) for reject/return actions
  const [toast, setToast] = useState({ open: false, appearance: "", body: "" });
  const account = userInfo;
  const rolesDetails = roles;

  // Event_Approver__c access (per-charge, comma/semicolon/space/slash/pipe-separated emails)
  // Used to gate Event Approval (approve/reject/return) for a given charge row.
  const getHasAccessForVestas = (charge) => {
    if (!(rolesDetails && userInfo && charge)) return false;
    const accountEmail = account.toLowerCase();

    const approverString = charge.Event_Approver__c || '';
    const approverList = approverString
      .split(/[\s,;/|]+/)
      .map(email => email.trim().toLowerCase())
      .filter(email => email && /\S+@\S+\.\S+/.test(email));

    // Distribution bypass rule
    const distributionBypassUsers = [
      "torez@vestas.com",
      "washl@vestas.com",
      "uhala@vestas.com",
      "crsmo@vestas.com",
      "helez@vestas.com",
      "rajki@vestas.com"
    ];

    const hasGlobalDistribution = approverList.includes("globaldistribution@vestas.com");

    // 🔥 1) If globaldistribution is in approverList → auto-access for 4 users
    if (hasGlobalDistribution && distributionBypassUsers.includes(accountEmail)) {
      return true;
    }

    // 🔥 2) Normal access logic
    return (
      (rolesDetails.includes('IDAVestasApprover') &&
        approverList.includes(accountEmail)) ||
      accountEmail === "abhay.mishra@maersk.com" ||
      accountEmail === "subas.sunder@maersk.com" ||
      accountEmail === "sriram.n@maersk.com" ||
      accountEmail === "raksha.n@maersk.com" ||
      accountEmail === "soham.chatterjee@maersk.com" || accountEmail === 'sunil.bhandari.s@lns.maersk.com' || accountEmail === "sai.priya@maersk.com"
    );
  };

  // Cost_Approver__c access (per-charge, comma/semicolon/space/slash/pipe-separated emails)
  // Used to gate Cost Approval (approve/reject/return) for Non-MRS charges.
  const getHasAccessForCostApprover = (charge) => {
    if (!(rolesDetails && userInfo && charge)) return false;
    const accountEmail = account.toLowerCase();
    const costApproverString = charge.Cost_Approver__c || '';
    const costApproverList = costApproverString
      .split(/[\s,;/|]+/)
      .map(email => email.trim().toLowerCase())
      .filter(email => email && /\S+@\S+\.\S+/.test(email));

    return (
      (rolesDetails.includes('IDAVestasApprover') &&
        costApproverList.includes(accountEmail)) ||
      accountEmail === "abhay.mishra@maersk.com" ||
      accountEmail === "subas.sunder@maersk.com" ||
      accountEmail === "sriram.n@maersk.com" ||
      accountEmail === "raksha.n@maersk.com" ||
      accountEmail === "soham.chatterjee@maersk.com" ||
      accountEmail === 'sunil.bhandari.s@lns.maersk.com' ||
      accountEmail === "sai.priya@maersk.com"
    );
  };

  // Bypass for OLD Non-MRS charges created before the Cost Approver feature was introduced.
  // If the charge is Non-MRS, was created before 2026-09-15, and has no Cost_Approver__c set,
  // treat it as auto cost-approved once it reaches "Event Charges Approved" status.
  const CUTOFF_DATE = new Date('2026-09-15T00:00:00Z');
  const NON_MRS_PREFIXES_GLOBAL = ["non mrs", "oce unplanned charge (non mrs)", "air unplanned charge (non mrs)", "oog unplanned charge (non mrs)", "oce charge deviation (mrs)", "air charge deviation (mrs)", "oog charge deviation (mrs)"];
  const isOldNonMrsCostApproverBypass = (charge) => {
    if (!charge) return false;
    const mrsNonMrs = (charge.MRS_NON_MRS__c || "").toLowerCase();
    const isNonMrsType = NON_MRS_PREFIXES_GLOBAL.some(p => mrsNonMrs.startsWith(p));
    if (!isNonMrsType) return false;

    const costApproverString = (charge.Cost_Approver__c || '').trim();
    if (costApproverString) return false; // has a cost approver set, no bypass

    if (!charge.CreatedDate) return false;
    const created = new Date(charge.CreatedDate);
    if (isNaN(created.getTime())) return false;

    return created < CUTOFF_DATE;
  };

  let hasAccessForMaersk = false;
  if (rolesDetails && account && userInfo) {
    hasAccessForMaersk = (rolesDetails.includes('IDAMaerskBilling'));
  }
  let hasVestasReadAccess;
  if (rolesDetails && userInfo) {
    hasVestasReadAccess = rolesDetails.includes('IDAVestasApprover') || rolesDetails.includes('IDAVestasViewer') || account.toLowerCase() === "subas.sunder@maersk.com" || account.toLowerCase() === "nelluri.sriram@maersk.com";
  }

  // Fetch charges for this notice
  useEffect(() => {
    const fetchCharges = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/all-changes?noticeId=${id}`, {
          headers: {
            "Authorization": `Bearer ${token}`,
            "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
          }
        });
        const json = await res.json();

        setCharges(json.charges || []); // ✅ set charges from json.charges
      } catch (err) {
        console.error("Error fetching charges:", err);
        setCharges([]); // optional: reset charges on error
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCharges(); // only fetch if id is defined
    }
  }, [id]);


  const handleDownloadFiles = async () => {
    const chargeId = id || parentNotice?.Parent_Id;
    if (!chargeId) {
      console.log("⚠️ No chargeId for download");
      return;
    }

    console.log("📥 Starting download for chargeId:", chargeId);
    setDownloadAttempted(true);
    setFilesLoading(true);

    try {
      const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);

      console.log("🌐 Fetching files from:", `${process.env.REACT_APP_API_BASE_URL}/get-charge-files?charge_id=${chargeId}`);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get-charge-files?charge_id=${chargeId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
        }
      });

      console.log("📊 Download response status:", response.status);
      console.log("📊 Download response headers:", Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob from response
      const blob = await response.blob();
      console.log("📦 Blob received - size:", blob.size, "type:", blob.type);

      if (blob.size === 0) {
        alert("No files available for download.");
        return;
      }

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${chargeId}_files.zip`;
      document.body.appendChild(link);
      link.click();

      console.log("✅ Download initiated successfully");

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("❌ Error downloading files:", err);
      alert(`Failed to download files: ${err.message}`);
    } finally {
      setTimeout(() => setFilesLoading(false), 2000);
    }
  };



  // state 
  const [parentLoading, setParentLoading] = useState(true); // Always start loading since we always fetch

  // Always fetch parent notice to ensure we have the latest data
  useEffect(() => {
    if (id) {
      const fetchNotice = async () => {
        try {
          setParentLoading(true);
          const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
          const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/notice/${id}`, {
            headers: {
              "Authorization": `Bearer ${token}`,
              "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
            }
          });
          const data = await res.json();
          setParentNotice(data);
        } catch (err) {
          console.error("Error fetching parent notice:", err);
        } finally {
          setParentLoading(false);
        }
      };
      fetchNotice();
    }
  }, [id]); // Only depend on id, always fetch fresh data

  useEffect(() => {
    const chargeId = id || parentNotice?.Parent_Id;
    if (!chargeId) {
      console.log("⚠️ No chargeId available yet");
      return;
    }

    const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
    const checkFilesExist = async () => {
      try {
        console.log("🔍 Checking if files exist for chargeId:", chargeId);
        
        // Use GET with a small byte range to check without downloading the full file
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get-charge-files?charge_id=${chargeId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY,
            'Range': 'bytes=0-0' // Request only the first byte
          }
        });

        console.log("📊 Response status:", res.status);
        console.log("📊 Response headers:", Object.fromEntries(res.headers.entries()));

        // Check if files exist based on response status and content-length
        // Status 200 (OK) or 206 (Partial Content) means files exist
        const contentLength = res.headers.get('content-length');
        const contentType = res.headers.get('content-type');
        
        // If it's a zip file and response is successful, files exist
        const hasFilesAvailable = (res.status === 200 || res.status === 206) && 
                                 (contentType?.includes('zip') || contentType?.includes('octet-stream'));
        
        console.log("✅ Files available:", hasFilesAvailable);
        console.log("📦 Content type:", contentType);
        console.log("📦 Content length:", contentLength);
        
        setHasFiles(hasFilesAvailable);
      } catch (error) {
        console.error("❌ Error checking files:", error);
        // On error, assume files might exist (fail open)
        setHasFiles(true);
      }
    };
    
    checkFilesExist();
  }, [id, parentNotice?.Parent_Id]);



  // window.handleAction = (chargeId, actionType) => {
  //   if (actionType === "approve") {
  //     setSelectedChargeId(chargeId);
  //     setModalActionType("approve");
  //     setApproveModalOpen(true);  // open approve modal
  //   } else {
  //     setSelectedChargeId(chargeId);
  //     setModalActionType(actionType);
  //     setModalOpen(true); // reject/return modal
  //   }
  // };
  const handleLogout = () => {
    AL.logoutRedirect({
      post_logout_uri: window.location.origin + "/"
    });
  };
  window.handleAction = (chargeId, actionType, number = null, chargeStatus = null) => {
    // Reset all modals first
    setModalOpen(false);
    setApproveModalOpen(false);
    setPoModalOpen(false);
    setInvoiceModalOpen(false);

    // Set the charge ID and immediately handle the action
    setSelectedChargeId(chargeId);

    if (actionType === "approve") {
      // Determine if it's cost approval (isBot=true) or event approval (isBot=false)
      // Cost approval: when status is "Event Charges Approved"
      // Event approval: when status is "Awaiting Customer Response" or rejected/returned states
      const isBot = chargeStatus === "Event Charges Approved" || chargeStatus === "Cost Charges Rejected" || chargeStatus === "Cost Charges Returned";
      setIsApprovalTypeBot(isBot);
      setModalActionType("approve");
      setTimeout(() => setApproveModalOpen(true), 10);
    } else if (actionType === "reject" || actionType === "return") {
      // Determine if it's cost rejection/return (isBot=true) or event rejection/return (isBot=false)
      // Cost reject/return: when status is "Event Charges Approved"
      // Event reject/return: when status is "Awaiting Customer Response"
      const isBot = chargeStatus === "Event Charges Approved" || chargeStatus === "Cost Charges Rejected" || chargeStatus === "Cost Charges Returned";
      setIsActionTypeBot(isBot);
      setModalActionType(actionType);
      setTimeout(() => setModalOpen(true), 10);
    } else if (actionType === "poNumber") {
      setSelectedPONumber(number);
      setTimeout(() => {
        setPoModalOpen(true);
      }, 10);
    } else if (actionType === "invoiceNumber") {
      setSelectedInvoiceNumber(number);
      setTimeout(() => {
        setInvoiceModalOpen(true);
      }, 10);
    } else {
      setModalActionType(actionType);
      setTimeout(() => setModalOpen(true), 10);
    }
  };



  // In ChargesPage.jsx


  const handleGoBack = () => {
    navigate('/');
  };
  // In ChargesPage.jsx
  window.handleViewComments = (chargeId) => {
    navigate(`/comments/${chargeId}`);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "-";
    try {
      const date = new Date(timestamp);
      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch (err) {
      console.error("Error formatting timestamp:", err);
      return timestamp; // Return original if formatting fails
    }
  };
const hasInvalidRows = charges.some(c => (c.Status__c || "").toLowerCase().includes("invalid"));
  return (
    <div className="mds">
      <McTopBar
        product="Maersk | Unplanned Charges"
        onClick={()=>{navigate('/')}}
        productshort="Maersk"
        style={{
          paddingLeft: "20px",
          height: "64px",
          paddingRight: "20px",
          marginTop: "-5px",
          borderBottom: "1px solid #DBDBDB",
        }}
      >

        <div slot="actions" style={{ display: "flex", alignItems: "center", color: "white" }}>
          <McButton
            onClick={handleGoBack}
            label="Go to Home"
            appearance="primary"
            fit="small"
            style={{ marginRight: "1rem" }}
          />
          <McButton onClick={handleLogout} label="Sign Out" appearance="secondary" fit="small" />
        </div>
      </McTopBar>

      <div className="charges-container mds">
        <h2 style={{ marginTop: "10px", paddingLeft: "1rem" }} className="main-header">
          List of All Charges
        </h2>

        {loading ? (
          <div className="loader-container">
            <div className="loader"></div>
            <p>Loading charge details...</p>
          </div>
        ) : (
          <>

            {/* Parent Notice Summary */}
            {parentLoading ? (
              <div
                className="notice-summary"
                style={{
                  margin: "1rem",
                  padding: "1rem",
                  background: "#f9f9f9",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                  textAlign: "center",
                }}
              >
                <div className="loader"></div>

              </div>
            ) : parentNotice ? (
              <div
                className="notice-summary"
                style={{
                  margin: "1rem",
                  padding: "1rem",
                  background: "#f9f9f9",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                }}
              >
                <h3 className="parent-heading">Charge Notice Details</h3>
                <table className="notice-summary-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    <tr>
                      <td style={{ fontWeight: "bold", padding: "8px" }}>Charge Notice</td>
                      <td style={{ padding: "8px" }}>
                        <TextWithTooltip text={parentNotice.Name || "NA"} />
                      </td>
                      <td style={{ fontWeight: "bold", padding: "8px" }}>Status</td>
                      <td style={{ padding: "8px" }}>
                        <TextWithTooltip text={parentNotice.Status__c || "NA"} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: "bold", padding: "8px" }}>FO Number</td>
                      <td style={{ padding: "8px" }}>
                        <TextWithTooltip text={parentNotice.FO_Number__c || "NA"} />
                      </td>
                      <td style={{ fontWeight: "bold", padding: "8px" }}>Plant</td>
                      <td style={{ padding: "8px" }}>
                        <TextWithTooltip text={parentNotice.Plant__c || "NA"} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: "bold", padding: "8px" }}>Origin</td>
                      <td style={{ padding: "8px" }}>
                        <TextWithTooltip text={parentNotice.Place_Of_Receipt_City_Name__c || "NA"} />
                      </td>
                      <td style={{ fontWeight: "bold", padding: "8px" }}>Destination</td>
                      <td style={{ padding: "8px" }}>
                        <TextWithTooltip text={parentNotice.Place_Of_Delivery_City_Name__c || "NA"} />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ fontWeight: "bold", padding: "8px" }}>Segment</td>
                      <td style={{ padding: "8px" }}>
                        <TextWithTooltip text={parentNotice.Segment__c || "NA"} />
                      </td>
                      <td style={{ fontWeight: "bold", padding: "8px" }}>Shipper Name</td>
                      <td style={{ padding: "8px" }}>
                        <TextWithTooltip text={parentNotice.Ship_From_Name__c || "NA"} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p style={{ margin: "1rem", color: "gray" }}>
                No parent notice found.
              </p>
            )}



            {/* Attachments Section - Always show download button */}
            {hasFiles && (
              <div
                style={{
                  margin: "1rem",
                  padding: "1rem",
                  background: "#f9f9f9",
                  borderRadius: "8px",
                  border: "1px solid #ddd",
                }}
              >

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                  <h3 style={{ margin: 0 }}>Attachments</h3>

                  <McButton
                    label={filesLoading ? "Downloading..." : downloadAttempted ? "Download Again" : "Download Files"}
                    appearance="primary"
                    fit="small"
                    disabled={filesLoading}
                    onClick={handleDownloadFiles}
                  />


                </div>
                <p style={{ color: "#666", margin: 0 }}>
                  {filesLoading ?
                    "Please wait while we prepare your files..." :
                    "Click to download a ZIP file containing all attachments for this charge notice. If no files are available, you will be notified."
                  }
                </p>
              </div>)}


            {/* Charges Table */}
            <div style={{ height: "70vh", overflowY: "auto", padding: "10px" }}>
              <McTable
                data={charges}
                datakey="Id"
                zebrastripes
                fit="small"
                columns={[
                  {
                    id: "Name",
                    label: "Charge ID",
                    cellTemplate: (e) => {
                      const val = e.rowData.Name || "-";
                      const isDuplicate = e.rowData.Duplicate_Exception_Check__c === true;
                      if (isDuplicate) {
                        return e.html`
                          <span class="fo-duplicate-wrapper" style="display:inline-flex;align-items:center;gap:6px;">
                            <span class="fo-duplicate-value" style="color:#dc2626;font-weight:600;">${val}</span>
                            <mc-tag label="Duplicate" appearance="error" fit="small" style="margin-left:6px"></mc-tag>
                          </span>`;
                      }
                      return e.html`<span>${val}</span>`;
                    }
                  },
                  {
                    id: "Material_Name__c", 
                    label: "Material Name",
                    cellTemplate: (e) => {
                      const val = e.rowData.Material_Name__c || "-";
                      const displayVal = val.toString();
                      const isLengthy = displayVal.length > 20;
                      const materialDescription = e.rowData.Charge_Description__c || "";
                      
                      let tooltipText = '';
                      // For Material Name:
                      // - If truncated + description: "Material Name: ... / Description: ..."
                      // - If truncated + no description: "Full Name"
                      // - If not truncated + description: just description
                      // - If not truncated + no description: full name
                      if (isLengthy) {
                        tooltipText = materialDescription ? `Material Name: ${displayVal}\nDescription: ${materialDescription}` : displayVal;
                      } else {
                        tooltipText = materialDescription || displayVal;
                      }

                      return e.html`
                        <span 
                          title="${tooltipText}" 
                          class="table-cell-content ${isLengthy ? 'lengthy-content' : ''}"
                          data-tooltip="${tooltipText}"
                        >
                          ${isLengthy ? displayVal.substring(0, 17) + '...' : displayVal}
                        </span>`;
                    }
                  },
                  {
                    id: "Unit_Price__c",
                    label: "Unit Price",
                    cellTemplate: (e) =>
                      e.html`${e.rowData.Unit_Price__c != null ? `${e.rowData.Unit_Price__c}` : "-"}`,
                  },
                  {
                    id: "MRS_NON_MRS__c",
                    label: "MRS / Non-MRS",
                    cellTemplate: (e) =>
                      e.html`${e.rowData.MRS_NON_MRS__c != null ? e.rowData.MRS_NON_MRS__c : "-"}`,
                  },
                  {
                    id: "Awaiting_Customer_Response_Timestamp",
                    label: "Awaiting Customer Response",
                    cellTemplate: (e) =>
                      e.html`${formatTimestamp(e.rowData.Awaiting_Customer_Response_Timestamp)}`,
                  },
                  {
                    id: "Quantity__c",
                    label: "Quantity",
                    cellTemplate: (e) =>
                      e.html`${e.rowData.Quantity__c != null ? e.rowData.Quantity__c : "-"}`,
                  },
                  {
                    id: "Amount__c",
                    label: "Amount",
                    cellTemplate: (e) =>
                      e.html`${e.rowData.Amount__c != null ? `${e.rowData.Amount__c}` : "-"}`,
                  },
                  {
                    id: "Conversion_Rate_USD__c",
                    label: "Conversion Rate (USD)",
                    cellTemplate: (e) =>
                      e.html`${e.rowData.Conversion_Rate_USD__c != null ? `${e.rowData.Conversion_Rate_USD__c}` : "-"}`,
                  },
                  {
                    id: "Amount_in_USD__c",
                    label: "Amount in USD",
                    cellTemplate: (e) =>
                      e.html`${e.rowData.Amount_in_USD__c != null ? `${e.rowData.Amount_in_USD__c}` : "-"}`,
                  },
                  {id: "Currency__c", label: "Currency"},
                  {
                    id: "Invoice_Number__c",
                    label: "Invoice Number",
                    cellTemplate: (e) =>
                      e.html`${e.rowData.Invoice_Number__c != null ? `${e.rowData.Invoice_Number__c}` : "-"}`,
                  },
                  {
                    id: "PO_Number__c",
                    label: "PO Number",
                    cellTemplate: (e) =>
                      e.html`${e.rowData.PO_Number__c != null ? `${e.rowData.PO_Number__c}` : "-"}`,
                  },
                  {
                    id: "Status__c",
                    label: "Status",
                    cellTemplate: (e) => {
                      const status = e.rowData.Status__c;
                      const appearance =
                        status === "Charges Rejected"
                          ? "error"
                          : status === "Awaiting Customer Response"
                            ? "warning"
                            : status
                              ? "success"
                              : "";
                      return status
                        ? e.html`<mc-tag label="${status}" appearance="${appearance}"></mc-tag>`
                        : e.html`<span></span>`;
                    },
                  },
                   ...(hasInvalidRows ? [{
                    id: "Reason_For_Invalid_Charge__c",
                    label: "Reason for Invalid",
                    cellTemplate: (e) => {
                      const reason = e.rowData.Reason_For_Invalid_Charge__c;
                      if (!reason) return e.html`<span style="color:#aaa;">-</span>`;
                      return e.html`
                        <span
                          title="${reason}"
                          style="
                            display: inline-flex;
                            align-items: center;
                            gap: 5px;
                            max-width: 220px;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                            color: #b45309;
                            font-size: 13px;
                            font-weight: 500;
                            background: #fff7ed;
                            border: 1px solid #fed7aa;
                            border-radius: 6px;
                            padding: 2px 8px;
                            cursor: help;
                          ">
                          ⚠ ${reason.length > 28 ? reason.substring(0, 28) + "…" : reason}
                        </span>`;
                    },
                  }] : []),
                  {
                    id: "Comments__c",
                    label: "Comments",
                    cellTemplate: (e) =>
                      e.html`<span title="${e.rowData.Comments__c || ""}" style="max-width:250px; display:inline-block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">
            ${e.rowData.Comments__c || ""}
          </span>`,
                  },
                  {
                    id: "Event_Approver__c",
                    label: "Event Approver",
                    cellTemplate: (e) => {
                      const val = e.rowData.Event_Approver__c || "NA";
                      return e.html`<span title="${val}" style="max-width:200px; display:inline-block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${val}</span>`;
                    },
                  },
                  {
                    id: "Cost_Approver__c",
                    label: "Cost Approver",
                    cellTemplate: (e) => {
                      const val = e.rowData.Cost_Approver__c || "NA";
                      return e.html`<span title="${val}" style="max-width:200px; display:inline-block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${val}</span>`;
                    },
                  },
                  {
                    id: "PO_Creator__c",
                    label: "PO Creator",
                    cellTemplate: (e) => {
                      const val = e.rowData.PO_Creator__c || "NA";
                      return e.html`<span title="${val}" style="max-width:200px; display:inline-block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${val}</span>`;
                    },
                  },
                   {
                    id: "PO Number__c",
                    label: "Confirm PO",
                    cellTemplate: (e) => {
                      const status = (e.rowData.Status__c || "").toLowerCase();
                      // PO Confirm requires Cost Approval for BOTH MRS and Non-MRS charges
                      // (for MRS, Cost Approval happens via the bot API rather than the Cost Approvals buttons).
                      // Exception: old Non-MRS charges (created before 2026-09-15) with no Cost_Approver__c
                      // set are auto-approved for cost once they reach "Event Charges Approved" status,
                      // since the Cost Approver requirement did not exist for them.
                      const bypassCostApproval = isOldNonMrsCostApproverBypass(e.rowData);
                      const isApproved = status === "cost charges approved" ||
                        (bypassCostApproval && status === "event charges approved");
                      const poNumber = e.rowData.PO_Number__c; // 👈 get this row’s PO number
                      const poCreators = (e.rowData.PO_Creator__c || "")
                        .split(",")
                        .map(email => email.trim().toLowerCase())
                        .filter(Boolean);

                      const isPOCreator = poCreators.includes(account.toLowerCase().trim());

                      // Confirm PO only enables once Cost Approval has happened, and only for the PO Creator on this charge.
                      const enableConfirmPO = isApproved && isPOCreator;


                      return e.html`
                      <div style="display:flex; align-items:center; gap:6px;">
                        <button 
                          style="
                            padding: 4px 10px; 
                            font-size: 13px; 
                            font-weight: 500;
                            border-radius: 6px; 
                            border: 1px solid ${enableConfirmPO ? '#0070d2' : '#ccc'}; 
                            background-color: ${enableConfirmPO ? '#0070d2' : '#e0e0e0'};
                            color: ${enableConfirmPO ? 'white' : '#888'};
                            cursor: ${enableConfirmPO ? 'pointer' : 'not-allowed'};
                            transition: all 0.2s ease-in-out;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.15);
                          "
                         
                          ${enableConfirmPO ? "" : "disabled"}
                          ${enableConfirmPO ?
                          `onmouseover="this.style.backgroundColor='#005bb5'; this.style.borderColor='#005bb5';"` +
                          `onmouseout="this.style.backgroundColor='#0070d2'; this.style.borderColor='#0070d2';"`
                          : ""}
                          onclick="${enableConfirmPO ?`window.handleAction('${e.rowData.Id}', 'poNumber', '${poNumber}')` : ""}">
                          Confirm
                        </button>
                      </div>
                    `;
                    }
                  },
                  {
                    id: "Invoice Number__c",
                    label: "Confirm Invoice",
                    cellTemplate: (e) => {
                      const isPOCreated = e.rowData.Status__c === "PO Created";
                      const invoiceNumber = e.rowData.Invoice_Number__c;
                      const invoiceCreators = (e.rowData.Invoice_Creator__c || "")
                        .split(/[\s,;/|]+/)
                        .map(email => email.trim().toLowerCase())
                        .filter(Boolean);
                      const isInvoiceCreator = invoiceCreators.includes(account.toLowerCase().trim());
                      // Confirm Invoice only enables once PO is Created, and only for the Invoice Creator on this charge
                      // (Maersk Billing keeps access too, same as before this field existed).
                      const isEnabled = isPOCreated && (isInvoiceCreator || hasAccessForMaersk);
                      return e.html`
                      <div style="display:flex; align-items:center; gap:6px;">
                        <button 
                          style="
                            padding: 4px 10px; 
                            font-size: 13px; 
                            font-weight: 500;
                            border-radius: 6px; 
                            border: 1px solid ${isEnabled ? '#0070d2' : '#ccc'}; 
                            background-color: ${isEnabled ? '#0070d2' : '#e0e0e0'}; 
                            color: ${isEnabled ? 'white' : '#888'}; 
                            cursor: ${isEnabled ? 'pointer' : 'not-allowed'};
                            transition: all 0.2s ease-in-out;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.15);
                          "
                          ${isEnabled ? "" : "disabled"}
                          ${isEnabled ?
                          `onmouseover="this.style.backgroundColor='#005bb5'; this.style.borderColor='#005bb5';"` +
                          `onmouseout="this.style.backgroundColor='#0070d2'; this.style.borderColor='#0070d2';"`
                          : ""}
                          onclick="${isEnabled ? `window.handleAction('${e.rowData.Id}', 'invoiceNumber', '${invoiceNumber}')` : ""}">
                          Confirm
                        </button>
                      </div>
                    `;
                    }
                  },

                  {
                    id: "Actions",
                    label: "Event Approvals",
                    cellTemplate: (e) => {
                      const status = e.rowData.Status__c;

                      // Define button enable/disable logic based on status
                      const isAwaitingResponse = status === "Awaiting Customer Response";
                      const hasAccessForVestas = getHasAccessForVestas(e.rowData);
                      const approveEnabled = (isAwaitingResponse) && hasAccessForVestas;
                      const rejectEnabled = (isAwaitingResponse) && hasAccessForVestas;
                      const returnEnabled = (isAwaitingResponse) && hasAccessForVestas;

                      return e.html`<div style="display:flex; gap:4px;">
                    <mc-button 
                      appearance="primary" 
                      fit="small" 
                      ${approveEnabled ? '' : 'disabled'}
                      style="${approveEnabled ? '' : 'opacity: 0.5; filter: grayscale(70%); cursor: not-allowed; pointer-events: none;'}"
                      onclick="${approveEnabled ? `window.handleAction('${e.rowData.Id}', 'approve', null, '${e.rowData.Status__c}')` : ''}">
                      Approve
                      
                    </mc-button>
                    <mc-button 
                      appearance="error" 
                      fit="small" 
                      ${rejectEnabled ? '' : 'disabled'}
                      style="${rejectEnabled ? '' : 'opacity: 0.5; filter: grayscale(70%); cursor: not-allowed; pointer-events: none;'}"
                      onclick="${rejectEnabled ? `window.handleAction('${e.rowData.Id}', 'reject')` : ''}">
                      Reject
                    </mc-button>
                    <mc-button 
                      appearance="secondary" 
                      fit="small" 
                      ${returnEnabled ? '' : 'disabled'}
                      style="${returnEnabled ? '' : 'opacity: 0.5; filter: grayscale(70%); cursor: not-allowed; pointer-events: none;'}"
                      onclick="${returnEnabled ? `window.handleAction('${e.rowData.Id}', 'return')` : ''}">
                      Return
                    </mc-button>
                  </div>`;
                    }
                  },
                  {
                    id: "Actions",
                    label: "Cost Approvals",
                    cellTemplate: (e) => {
                      const NON_MRS_PREFIXES = ["non mrs", "oce unplanned charge (non mrs)", "air unplanned charge (non mrs)", "oog unplanned charge (non mrs)","oce charge deviation (mrs)","air charge deviation (mrs)","oog charge deviation (mrs)"];
                      const mrsNonMrs = (e.rowData.MRS_NON_MRS__c || "").toLowerCase();
                      const status = (e.rowData.Status__c || "").toLowerCase();
                      const isNonMrsType = NON_MRS_PREFIXES.some(p => mrsNonMrs.startsWith(p));
                      const isAwaitingResponse = status === "event charges approved";

                      // Cost Approval is only ever done manually for Non-MRS charges, gated by
                      // Cost_Approver__c (+ admins) for that charge. For MRS charges the bot approves
                      // cost via API, so these buttons stay disabled regardless of access.
                      const costAccess = getHasAccessForCostApprover(e.rowData);

                      const approveEnabled = isNonMrsType && isAwaitingResponse && costAccess;
                      const rejectEnabled = isNonMrsType && isAwaitingResponse && costAccess;
                      const returnEnabled = isNonMrsType && isAwaitingResponse && costAccess;

                      return e.html`<div style="display:flex; gap:4px;">
                    <mc-button 
                      appearance="primary" 
                      fit="small" 
                      ${approveEnabled ? '' : 'disabled'}
                      style="${approveEnabled ? '' : 'opacity: 0.5; filter: grayscale(70%); cursor: not-allowed; pointer-events: none;'}"
                      onclick="${approveEnabled ? `window.handleAction('${e.rowData.Id}', 'approve', null, '${e.rowData.Status__c}')` : ''}">
                      Approve
                      
                    </mc-button>
                     <mc-button 
                      appearance="error" 
                      fit="small" 
                      ${rejectEnabled ? '' : 'disabled'}
                      style="${rejectEnabled ? '' : 'opacity: 0.5; filter: grayscale(70%); cursor: not-allowed; pointer-events: none;'}"
                      onclick="${rejectEnabled ? `window.handleAction('${e.rowData.Id}', 'reject', null, '${e.rowData.Status__c}')` : ''}">
                      Reject
                    </mc-button>
                    <mc-button 
                      appearance="secondary" 
                      fit="small" 
                      ${returnEnabled ? '' : 'disabled'}
                      style="${returnEnabled ? '' : 'opacity: 0.5; filter: grayscale(70%); cursor: not-allowed; pointer-events: none;'}"
                      onclick="${returnEnabled ? `window.handleAction('${e.rowData.Id}', 'return', null, '${e.rowData.Status__c}')` : ''}">
                      Return
                    </mc-button>
                  </div>`;
                    }
                  },
                  {
                    id: "ViewComments",
                    label: "Comments History",
                    cellTemplate: (e) =>
                      e.html`<mc-button 
                      appearance="primary"
                      variant="outlined" 
                      fit="small" 
                      onclick="window.handleViewComments('${e.rowData.Id}')">
                        <mc-icon icon="clipboard-text"></mc-icon>
                    </mc-button>`,
                  }



                ]}
              />
            </div>

          </>
        )}
      </div>

      {modalOpen && (
        <ActionModal
          open={modalOpen}
          chargeId={selectedChargeId}
          actionType={modalActionType}
          isBot={isActionTypeBot}
          onClose={() => setModalOpen(false)}
          onSuccess={(chargeId, status, comments) => {
            const updated = charges.map(c =>
              c.Id === chargeId ? { ...c, Status__c: status, Comments__c: comments } : c
            );
            setCharges(updated);
            // Auto reload page after action
            setTimeout(() => window.location.reload(), 500);
          }}
        />
      )}

      {approveModalOpen && (
        <ApproveModal
          open={approveModalOpen}
          chargeId={selectedChargeId}
          parentNotice={parentNotice}
          poNumber={parentNotice?.PO_Number__c}
          parentNoticeName={parentNotice?.Parent_Name__c || "NA"}
          userInfo={userInfo}
          isBot={isApprovalTypeBot}
          childId={charges.find(c => c.Id === selectedChargeId)?.Name || "NA"}
          onClose={() => setApproveModalOpen(false)}
          onSuccess={(chargeId, updatedStatus) => {
            const updated = charges.map(c =>
              c.Id === chargeId ? { ...c, Status__c: updatedStatus } : c
            );
            setCharges(updated);
            // Auto reload page after action
            setTimeout(() => window.location.reload(), 500);
          }}
        />
      )}

      <PoNumberModal
        open={poModalOpen}
        chargeId={selectedChargeId}
        poNumber={selectedPONumber}
        parentInvoiceNo={parentNotice?.Parent_Invoice_Number__c}
        onClose={() => setPoModalOpen(false)}
        onSuccess={(chargeId, updatedPONumber) => {
          const updated = charges.map(c =>
            c.Id === chargeId ? { ...c, PO_Number__c: updatedPONumber } : c
          );
          setCharges(updated);
          // Auto reload page after action
          setTimeout(() => window.location.reload(), 500);
        }}
      />

      <InvoiceModal
        open={invoiceModalOpen}
        chargeId={selectedChargeId}
        invoiceNumber={selectedInvoiceNumber}
        onClose={() => setInvoiceModalOpen(false)}
        onSuccess={(chargeId, updatedInvoiceNumber) => {
          const updated = charges.map(c =>
            c.Id === chargeId ? { ...c, Invoice_Number__c: updatedInvoiceNumber } : c
          );
          setCharges(updated);
          // Auto reload page after action
          setTimeout(() => window.location.reload(), 500);
        }}
      />

      {commentsModalOpen && (
        <CommentsHistoryModal
          open={commentsModalOpen}
          chargeId={selectedChargeId}
          onClose={() => setCommentsModalOpen(false)}
        />
      )}

      <McToast
        position="top-center"
        open={toast.open}
        close={() => setToast((p) => ({ ...p, open: false }))}
      >
        <McNotification body={toast.body} appearance={toast.appearance || "neutral"} />
      </McToast>
    </div>
  );
};

export default ChargesPage;